import React from "react";
import { Form, Row, Col, Badge } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { FaRobot, FaInfoCircle, FaBrain } from "react-icons/fa";
import { Icon } from "../../../../../components/common/Icon";
import { ChatbotFormData, ChatbotSettings } from "../types";
import RetrievalSettings from "../training/RetrievalSettings";
import { RiChatVoiceAiLine } from "react-icons/ri";
import { TbKey } from "react-icons/tb";
import { AIChatbot } from "../../../../../features/ai_chatbots/aiChatbotsService";

interface AIConfigTabProps {
  formData: ChatbotFormData;
  handleInputChange: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => void;
  settingsData: ChatbotSettings;
  setSettingsData: React.Dispatch<React.SetStateAction<ChatbotSettings>>;
  setFormData?: React.Dispatch<React.SetStateAction<ChatbotFormData>>;
  isMarketingSpecialist?: boolean;
  chatbot?: AIChatbot | null;
}

const AIConfigTab: React.FC<AIConfigTabProps> = ({
  formData,
  handleInputChange,
  settingsData,
  setSettingsData,
  setFormData,
  isMarketingSpecialist,
  chatbot,
}) => {
  const { t } = useTranslation();

  // Sadece 3 preset: en iyi kalite, en ucuz, en hızlı
  const MODEL_PRESETS: { value: string; labelKey: string; cost: string; speed: string }[] = [
    { value: "gpt-5.2", labelKey: "model_preset_best", cost: t("chatbots_edit.cost_high"), speed: t("chatbots_edit.speed_medium") },
    { value: "gpt-4.1", labelKey: "model_preset_fast", cost: t("chatbots_edit.cost_medium"), speed: t("chatbots_edit.speed_fast") },
    { value: "gpt-4.1-mini", labelKey: "model_preset_cheap", cost: t("chatbots_edit.cost_low"), speed: t("chatbots_edit.speed_fast") },
  ];

  const presetValues = MODEL_PRESETS.map((p) => p.value);
  const currentModel = settingsData?.provider?.model;
  const modelOptions =
    currentModel && !presetValues.includes(currentModel)
      ? [{ value: currentModel, labelKey: "", cost: "-", speed: "-" }, ...MODEL_PRESETS]
      : MODEL_PRESETS;

  const getModelInfo = (modelValue: string) => {
    const found = MODEL_PRESETS.find((p) => p.value === modelValue);
    if (found) return { name: t(`chatbots_edit.${found.labelKey}`), cost: found.cost, speed: found.speed };
    return { name: modelValue, cost: "-", speed: "-" };
  };

  const handleModelChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newModel = e.target.value;
    setSettingsData((prev) => ({
      ...prev,
      provider: {
        ...prev.provider,
        model: newModel,
      },
    }));
    // Sync with formData.model if setFormData is available
    if (setFormData) {
      setFormData((prev) => ({
        ...prev,
        model: newModel,
      }));
    }
  };

  const selectedModelInfo = getModelInfo(settingsData.provider.model);

  return (
    <div className="tab-pane-content">

      <Row>
        <Col md={6}>
          <Form.Group className="form-group-custom">
            <Form.Label className="form-label-custom">
              <Icon icon={FaRobot} className="me-2" />
              {t("chatbots_edit.ai_provider")}
            </Form.Label>
            <Form.Select
              name="ai_provider"
              value="openai"
              className="form-control-custom"
              disabled
            >
              <option value="openai">OpenAI (GPT)</option>
            </Form.Select>
          </Form.Group>
        </Col>
        <Col md={6}>
          <Form.Group className="form-group-custom">
            <Form.Label className="form-label-custom">
              <Icon icon={FaBrain} className="me-2" />
              {t("chatbots_edit.model")}
            </Form.Label>
            <Form.Select
              name="ai_model"
              value={settingsData.provider.model}
              onChange={handleModelChange}
              className="form-control-custom"
            >
              {modelOptions.map((preset) => (
                <option key={preset.value} value={preset.value}>
                  {preset.labelKey ? t(`chatbots_edit.${preset.labelKey}`) : preset.value}
                </option>
              ))}
            </Form.Select>
            {selectedModelInfo && (
              <div className="model-info-badges mt-2">
                <span className="badge bg-light text-dark border me-2">
                  {t("chatbots_edit.speed")}: {selectedModelInfo.speed}
                </span>
                <span className="badge bg-light text-dark border">
                  {t("chatbots_edit.cost")}: {selectedModelInfo.cost}
                </span>
              </div>
            )}
          </Form.Group>
        </Col>
      </Row>

      <div className="chatbots-edit-tab-content-header">
        <div className="icon">
          <TbKey />
        </div>
        <span>{t("api_keys")}</span>
      </div>
      <Form.Group className="form-group-custom">
        <Form.Label className="form-label-custom">
          {t("openai_api_key")}
          {isMarketingSpecialist && (
            <Badge bg="warning" className="ms-2">
              {t("chatbots_edit.read_only")}
            </Badge>
          )}
        </Form.Label>
        <Form.Control
          type="text"
          name="openai_api_key"
          value={formData.openai_api_key}
          onChange={handleInputChange}
          placeholder={chatbot?.openai_api_key_set ? t("chatbots_edit.api_key_placeholder_new") : "sk-..."}
          className="form-control-custom"
          readOnly={isMarketingSpecialist}
        />
        <Form.Text className="form-text-custom">
          <Icon icon={FaInfoCircle} className="me-1" />
          {isMarketingSpecialist
            ? t("chatbots_edit.api_key_agency_only")
            : chatbot?.openai_api_key_set && formData.openai_api_key && formData.openai_api_key.includes("...")
            ? t("chatbots_edit.api_key_masked_info")
            : chatbot?.openai_api_key_set
            ? t("chatbots_edit.api_key_set_info")
            : t("leave_empty_to_use_system_default")}
        </Form.Text>
      </Form.Group>

      <hr className="my-4 " />

      <RetrievalSettings
        settingsData={settingsData}
        setSettingsData={setSettingsData}
      />
    </div>
  );
};

export default AIConfigTab;
